import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

const ORDINAL_LABELS = ["first", "second", "third", "fourth", "fifth", "sixth"];

const STEP_OPTIONS = [
  { value: "big", label: "สูง" },
  { value: "small", label: "ต่ำ" },
  { value: "odd", label: "คี่" },
  { value: "even", label: "คู่" },
];

// "mix" (เลขชุดผสม) has a fixed meaning per position — สูงต่ำ + คู่คี่ +
// ตัดเลข — unlike the other step types, where every position is an
// equivalent, freely-chosen slot from the full STEP_OPTIONS list.
const HIGH_LOW_OPTIONS = [
  { value: "big", label: "สูง" },
  { value: "small", label: "ต่ำ" },
];
const ODD_EVEN_OPTIONS = [
  { value: "odd", label: "คี่" },
  { value: "even", label: "คู่" },
];

const SELECT_SX = {
  backgroundColor: "#fff",
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "#0a3a8c",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#b6c9ee" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0d4bb3" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ffd76b",
    borderWidth: 2,
  },
};

// The API leaves `digit` null for every "step"-stake reward type (it's
// only meaningful for "number"-stake codes), so it can't tell us how many
// positions to show. All known step types (e.g. step_front_mid_end) use
// 3 (front/mid/end), so fall back to that whenever digit isn't a real
// count.
const DEFAULT_STEP_POSITIONS = 3;

function StepModal({ open, stepName, stake, digitCount, onClose, onConfirm }) {
  const positions = ORDINAL_LABELS.slice(
    0,
    digitCount || DEFAULT_STEP_POSITIONS,
  );
  const [selection, setSelection] = useState({});

  // Start fresh each time the modal opens for a new "step" code.
  useEffect(() => {
    if (open) setSelection({});
  }, [open]);

  const allSelected = positions.every((pos) => selection[pos]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-gold-300/40">
        <span className="text-brand-gold-200 font-bold">รายการที่เลือก</span>
      </DialogTitle>
      <DialogContent className="bg-brand-blue-50">
        {stepName && (
          <div className="text-center pb-2">
            <span className="text-sm font-semibold text-brand-blue-800 bg-white border border-brand-gold-300 rounded-full px-3 py-1 shadow-sm">
              {stepName}
            </span>
          </div>
        )}
        <div className="flex flex-row gap-3 pt-3">
          {positions.map((pos, index) => (
            <div key={pos} className="flex flex-col flex-1 gap-1">
              {stake === "mix" && pos === "third" ? (
                <input
                  type="number"
                  className="border border-brand-blue-200 bg-white rounded-lg p-1.5 text-sm font-semibold text-brand-blue-800 focus:outline-none focus:ring-2 focus:ring-brand-gold-300 focus:border-brand-gold-300"
                  value={selection[pos] || ""}
                  min="0"
                  max="9"
                  onChange={(e) => {
                    const raw = e.target.value;
                    // min/max on a number input only affect the spinner
                    // arrows and validity state, not what can be typed, so
                    // clamp to a single digit (0-9) by hand.
                    const clamped =
                      raw === ""
                        ? ""
                        : String(
                            Math.min(9, Math.max(0, parseInt(raw, 10) || 0)),
                          );
                    setSelection((prev) => ({
                      ...prev,
                      [pos]: clamped,
                    }));
                  }}
                />
              ) : (
                <Select
                  size="small"
                  fullWidth
                  value={selection[pos] || ""}
                  onChange={(e) =>
                    setSelection((prev) => ({
                      ...prev,
                      [pos]: e.target.value,
                    }))
                  }
                  sx={SELECT_SX}
                  MenuProps={{
                    // Anchored right under the field (not the browser's
                    // native popup, which drifts to the page corner here).
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { sx: { mt: 0.5 } },
                  }}
                >
                  {(stake === "mix"
                    ? index === 0
                      ? HIGH_LOW_OPTIONS
                      : ODD_EVEN_OPTIONS
                    : STEP_OPTIONS
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
      <DialogActions className="bg-brand-blue-50">
        <button
          className="px-4 py-1.5 rounded text-brand-blue-700 font-semibold hover:bg-brand-blue-50 transition-colors"
          onClick={onClose}
        >
          ยกเลิก
        </button>
        <button
          disabled={!allSelected}
          className="px-4 py-1.5 rounded bg-gradient-to-b from-brand-blue-600 to-brand-blue-900 text-brand-gold-300 font-semibold shadow-sm hover:from-brand-blue-500 hover:to-brand-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => onConfirm(selection)}
        >
          ตกลง
        </button>
      </DialogActions>
    </Dialog>
  );
}

export default StepModal;
